/**
 * How to show a point's latest reading (web brief U7, as corrected by the
 * backend on 2026-09-27). The platform stores gateway quality as:
 * - `good` → quality_flag `valid`: shown normally;
 * - `uncertain` → `suspect`: shown, marked uncertain;
 * - `bad` → NO reading row at all (the gateway re-publishes a bad point about
 *   once a minute, and the platform drops it).
 * So a bad point is never a flag: it is a point with no recent value. Modbus
 * readings carry no quality_flag at all, which is normal.
 */

export type ReadingState = "valid" | "uncertain" | "stale" | "none";

export interface ReadingQuality {
  state: ReadingState;
  /** Short badge text ("" for a normal, fresh reading). */
  badge: string;
  /** A sentence for a tooltip or a line under the value. */
  hint: string;
}

/**
 * Seconds without a reading after which a point counts as stale: three polls,
 * and at least two minutes (deadbands and the gateway's once-a-minute bad
 * re-publish both leave gaps shorter than that). Five minutes when the poll
 * interval is unknown.
 *
 * A point with a heartbeat (its effective `max_silence_sec`) is re-sent at
 * least that often while it holds steady, so it is stale only after the
 * heartbeat plus two polls, and at least a minute, of slack.
 */
export function staleAfterSeconds(pollSec: number | null | undefined, heartbeatSec?: number | null): number {
  const poll = pollSec && pollSec > 0 ? pollSec : 0;
  if (heartbeatSec && heartbeatSec > 0) return heartbeatSec + Math.max(2 * poll, 60);
  if (!poll) return 300;
  return Math.max(3 * poll, 120);
}

export function readingQuality(
  latest: { time?: string | null; quality_flag?: string | null } | null | undefined,
  pollSec?: number | null,
  now: number = Date.now(),
  heartbeatSec?: number | null,
): ReadingQuality {
  const t = latest?.time ? new Date(latest.time).getTime() : NaN;
  if (!Number.isFinite(t) || t <= 0) {
    return { state: "none", badge: "", hint: "No reading yet." };
  }
  const ageS = (now - t) / 1000;
  if (ageS > staleAfterSeconds(pollSec, heartbeatSec)) {
    return {
      state: "stale",
      badge: "No recent value",
      hint: `Nothing arrived for ${heartbeatSec && heartbeatSec > 0 ? "longer than its heartbeat" : "several polls"}. The gateway may be failing to read this point (a bad read is not stored), or the device may be offline.`,
    };
  }
  if ((latest?.quality_flag ?? "").toLowerCase() === "suspect") {
    return { state: "uncertain", badge: "Uncertain", hint: "The gateway marked this value uncertain (for example, a stale read on the device)." };
  }
  return { state: "valid", badge: "", hint: "" };
}
